import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { createOvertime, getOvertimes } from "../../api/overtime";
import {
  convertToCompensatory,
  getCompensatoryPending,
} from "../../api/compensatory";
import { isPrimaryHrRole } from "../../utils/hrStaffHelpers";
import { mapOvertimeRow } from "../../utils/dataMappers";
import { employeeMatchesSearch } from "../../utils/employeeSearch";
import {
  filterMonthOvertimeGrants,
  formatPenaltyMonthLabel,
  getCurrentMonthKey,
  isApprovedOvertimeStatus,
  isPendingOvertimeStatus,
  settlementLabel,
  summarizeMonthOvertimeGrants,
} from "../../utils/overtimeGrantMonth";
import { getActiveAttendanceRegulation } from "../../api/attendanceRegulations";
import {
  applyOvertimeHourMultiplier,
  compensatoryOvertimeRateHint,
  DEFAULT_OVERTIME_HOUR_RATES,
  paidOvertimeRateHint,
  ratesFromRegulation,
} from "../../utils/overtimeHourRates";
import "./HrOvertimeGrant.css";

function employeeMatchesQuery(emp, query) {
  if (!String(query || "").trim()) return true;
  return employeeMatchesSearch(emp, query);
}

function pendingRowEmployee(row) {
  return {
    name: row?.employee?.fullName || row?.employee?.name,
    fullName: row?.employee?.fullName || row?.employee?.name,
    code: row?.employee?.employeeCode || row?.employee?.code,
    employeeCode: row?.employee?.employeeCode || row?.employee?.code,
  };
}

function grantRowEmployee(row) {
  return {
    name: row?.employeeName,
    fullName: row?.employeeName,
    code: row?.employeeCode,
    employeeCode: row?.employeeCode,
  };
}

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatGrantDate(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return String(value);
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
}

function formatGrantPeriod(row) {
  const start = formatGrantDate(row.startDate);
  const end = formatGrantDate(row.endDate);
  if (!row.endDate || row.endDate === row.startDate) return start;
  return `${start} → ${end}`;
}

function GrantStatusBadge({ status }) {
  if (isApprovedOvertimeStatus(status)) {
    return <span className="hr-overtime-grant__badge is-approved">معتمد</span>;
  }
  if (isPendingOvertimeStatus(status)) {
    return (
      <span className="hr-overtime-grant__badge is-pending">
        بانتظار الاعتماد
      </span>
    );
  }
  return <span className="hr-overtime-grant__badge">{status || "—"}</span>;
}

export default function HrOvertimeGrant() {
  const { user } = useAuth();
  const { employeesByBranch, refreshData, addRecentActivity } = useData();
  const isHrApprover = isPrimaryHrRole(user?.role);

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter(
          (e) =>
            e.status !== "terminated" &&
            String(e.id) !== String(user?.employeeId),
        ),
    [employeesByBranch, user?.employeeId],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [grantSearch, setGrantSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [hours, setHours] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [settlement, setSettlement] = useState("PAID_OT");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [grantRows, setGrantRows] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [overtimeRates, setOvertimeRates] = useState(
    DEFAULT_OVERTIME_HOUR_RATES,
  );

  const monthKey = getCurrentMonthKey();
  const monthLabel = formatPenaltyMonthLabel(monthKey);

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const rows = await getCompensatoryPending();
      setPendingRows(Array.isArray(rows) ? rows : []);
      setSelectedPendingIds([]);
    } catch {
      setPendingRows([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadMonthGrants = useCallback(async () => {
    setGrantsLoading(true);
    try {
      const payload = await getOvertimes();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      setGrantRows(rows.map(mapOvertimeRow));
    } catch {
      setGrantRows([]);
    } finally {
      setGrantsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
    loadMonthGrants();
    Promise.resolve()
      .then(() => getActiveAttendanceRegulation?.())
      .then((regulation) => setOvertimeRates(ratesFromRegulation(regulation)))
      .catch(() => setOvertimeRates(DEFAULT_OVERTIME_HOUR_RATES));
  }, [loadPending, loadMonthGrants]);

  const monthGrants = useMemo(
    () => filterMonthOvertimeGrants(grantRows),
    [grantRows],
  );
  const monthGrantSummary = useMemo(
    () => summarizeMonthOvertimeGrants(monthGrants),
    [monthGrants],
  );

  const filteredEmployees = useMemo(
    () => allEmployees.filter((e) => employeeMatchesQuery(e, searchTerm)),
    [allEmployees, searchTerm],
  );

  const filteredPendingRows = useMemo(
    () =>
      pendingRows.filter((row) =>
        employeeMatchesQuery(pendingRowEmployee(row), pendingSearch),
      ),
    [pendingRows, pendingSearch],
  );

  const filteredMonthGrants = useMemo(
    () =>
      monthGrants.filter((row) =>
        employeeMatchesQuery(grantRowEmployee(row), grantSearch),
      ),
    [monthGrants, grantSearch],
  );

  const selectedPending = pendingRows.filter((r) =>
    selectedPendingIds.includes(r.id),
  );
  const selectedPendingHours = selectedPending.reduce(
    (s, r) => s + Number(r.overtimeHours || 0),
    0,
  );

  function togglePendingId(id) {
    setSelectedPendingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function handleConvertCompensatory() {
    if (!selectedPendingIds.length) {
      setError("اختر يوماً واحداً على الأقل للتحويل");
      return;
    }
    setError("");
    setSuccess("");
    setPendingAction("convert");
    try {
      const result = await convertToCompensatory(selectedPendingIds);
      setSuccess(
        `تم تحويل ${result.totalHours} ساعة إلى رصيد إذن تعويضي لهذا الشهر`,
      );
      addRecentActivity?.({
        type: "compensatory",
        message: `تحويل ${result.totalHours} ساعة لرصيد تعويضي`,
      });
      await loadPending();
      await loadMonthGrants();
      await refreshData();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحويل الساعات لرصيد تعويضي"));
    } finally {
      setPendingAction("");
    }
  }

  async function handleGrantPaidFromPending() {
    if (!selectedPending.length) {
      setError("اختر يوماً واحداً على الأقل لمنح أوفر تايم مدفوع");
      return;
    }
    const employeeIds = [
      ...new Set(selectedPending.map((r) => r.employee?.id).filter(Boolean)),
    ];
    if (employeeIds.length !== 1) {
      setError("اختر أيام نفس الموظف فقط عند منح أوفر تايم مدفوع");
      return;
    }
    const emp = selectedPending[0].employee;
    const dates = selectedPending.map((r) => r.date).sort();
    const hoursNum =
      Math.round(selectedPendingHours * 100) / 100;

    setError("");
    setSuccess("");
    setPendingAction("paid");
    try {
      await createOvertime({
        employeeId: emp.id,
        hours: hoursNum,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        reason: "من ساعات زيادة الحضور",
        attendanceRecordIds: selectedPendingIds,
      });
      setSuccess(
        isHrApprover
          ? `تم منح ${hoursNum} ساعة إضافي لـ ${emp.fullName} واعتمادها`
          : `تم إرسال طلب إضافي ${hoursNum} ساعة لـ ${emp.fullName} بانتظار اعتماد الموارد البشرية`,
      );
      addRecentActivity?.({
        type: "overtime",
        message: `طلب إضافي ${hoursNum} ساعة لـ ${emp.fullName}`,
      });
      await loadPending();
      await loadMonthGrants();
      await refreshData();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إرسال طلب الإضافي"));
    } finally {
      setPendingAction("");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedEmployee?.id) {
      setError("اختر الموظف");
      return;
    }
    const hoursNum = Number(hours);
    if (!hoursNum || hoursNum < 0.25) {
      setError("أدخل عدد ساعات صحيح (0.25 فأكثر)");
      return;
    }
    if (!startDate || !endDate) {
      setError("حدد تاريخ البداية والنهاية");
      return;
    }
    if (endDate < startDate) {
      setError("تاريخ النهاية يجب أن يكون بعد أو يساوي تاريخ البداية");
      return;
    }

    setSubmitting(true);
    try {
      await createOvertime({
        employeeId: selectedEmployee.id,
        hours: hoursNum,
        startDate,
        endDate,
        reason: reason.trim() || undefined,
        settlement,
      });
      const settlementText =
        settlement === "COMPENSATORY"
          ? "ساعات إضافية (رصيد إذن تعويضي)"
          : "صرف مالي";
      setSuccess(
        isHrApprover
          ? `تم منح ${hoursNum} ساعة ${settlementText} لـ ${selectedEmployee.name || selectedEmployee.fullName} واعتمادها`
          : `تم إرسال طلب ${hoursNum} ساعة ${settlementText} لـ ${selectedEmployee.name || selectedEmployee.fullName} بانتظار اعتماد الموارد البشرية`,
      );
      addRecentActivity?.({
        type: "overtime",
        message: `طلب إضافي ${hoursNum} ساعة (${settlementText}) لـ ${selectedEmployee.name || selectedEmployee.fullName}`,
      });
      setHours("");
      setStartDate("");
      setEndDate("");
      setReason("");
      setSettlement("PAID_OT");
      setSelectedEmployee(null);
      await loadMonthGrants();
      await refreshData();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إرسال طلب الإضافي"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="hr-overtime-grant" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">منح ساعات إضافي / إذن تعويضي</h1>
        <p className="ess-home-subtitle">
          ساعات الزيادة من الحضور: حوّلها لرصيد إذن تعويضي أو امنحها كأوفر تايم
          مدفوع. المنح اليدوي يمكن أن يكون صرفاً مالياً أو ساعات إضافية. موظف
          الموارد البشرية يمنح ومدير الموارد البشرية يعتمد، وإذا منحها مدير
          الموارد البشرية تكون معتمدة مباشرة. الرصيد التعويضي لنفس الشهر فقط.
        </p>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      ) : null}

      <section className="hr-overtime-grant__panel hr-overtime-grant__pending">
        <h2>ساعات زيادة معلقة من الحضور (هذا الشهر)</h2>
        <p className="text-muted small">
          {paidOvertimeRateHint(overtimeRates.overtimePaidHourMultiplier)} ·{" "}
          {compensatoryOvertimeRateHint(
            overtimeRates.overtimeCompensatoryHourMultiplier,
          )}
        </p>
        {pendingLoading ? (
          <p className="text-muted">جاري التحميل...</p>
        ) : pendingRows.length === 0 ? (
          <p className="text-muted">لا توجد ساعات زيادة بانتظار التصرف</p>
        ) : (
          <>
            <label className="hr-overtime-grant__search-label" htmlFor="ot-pending-search">
              بحث في الساعات المعلقة
            </label>
            <input
              id="ot-pending-search"
              type="search"
              className="form-control mb-2"
              placeholder="بحث بالاسم أو الكود..."
              value={pendingSearch}
              onChange={(ev) => setPendingSearch(ev.target.value)}
            />
            <div className="hr-overtime-grant__pending-list">
              {filteredPendingRows.map((row) => (
                <label key={row.id} className="hr-overtime-grant__pending-row">
                  <input
                    type="checkbox"
                    checked={selectedPendingIds.includes(row.id)}
                    onChange={() => togglePendingId(row.id)}
                  />
                  <span className="hr-overtime-grant__pending-main">
                    <strong>
                      {row.employee?.fullName || "—"} (
                      {row.employee?.employeeCode || "—"})
                    </strong>
                    <span>
                      {row.date} — زيادة {row.overtimeHours} ساعة (عمل{" "}
                      {row.totalWorkingHours ?? "—"} ساعة)
                    </span>
                  </span>
                </label>
              ))}
              {filteredPendingRows.length === 0 ? (
                <p className="text-muted">لا يوجد موظفون مطابقون للبحث</p>
              ) : null}
            </div>
            <div className="hr-overtime-grant__pending-actions">
              <span className="text-muted">
                محدد: {selectedPendingIds.length} يوم /{" "}
                {Math.round(selectedPendingHours * 100) / 100} ساعة
              </span>
              <button
                type="button"
                className="btn btn-success"
                disabled={
                  !selectedPendingIds.length || Boolean(pendingAction)
                }
                onClick={handleConvertCompensatory}
              >
                {pendingAction === "convert"
                  ? "جاري التحويل..."
                  : "تحويل لرصيد إذن تعويضي"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={
                  !selectedPendingIds.length || Boolean(pendingAction)
                }
                onClick={handleGrantPaidFromPending}
              >
                {pendingAction === "paid"
                  ? "جاري الإرسال..."
                  : "منح كأوفر تايم مدفوع"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="hr-overtime-grant__panel hr-overtime-grant__month">
        <div className="hr-overtime-grant__month-head">
          <h2>منح الإضافي خلال {monthLabel}</h2>
          {monthGrantSummary.approvedCount || monthGrantSummary.pendingCount ? (
            <p className="hr-overtime-grant__month-summary">
              {monthGrantSummary.peopleCount} موظف معتمد ·{" "}
              {monthGrantSummary.approvedHours} ساعة معتمدة
              {monthGrantSummary.pendingCount
                ? ` · ${monthGrantSummary.pendingCount} بانتظار الاعتماد`
                : ""}
            </p>
          ) : null}
        </div>
        {grantsLoading ? (
          <p className="text-muted">جاري التحميل...</p>
        ) : monthGrants.length === 0 ? (
          <p className="text-muted">
            لا يوجد موظفون مُنح لهم إضافي معتمد خلال هذا الشهر
          </p>
        ) : (
          <>
            <label className="hr-overtime-grant__search-label" htmlFor="ot-grant-search">
              بحث في منح الشهر
            </label>
            <input
              id="ot-grant-search"
              type="search"
              className="form-control mb-2"
              placeholder="بحث بالاسم أو الكود..."
              value={grantSearch}
              onChange={(ev) => setGrantSearch(ev.target.value)}
            />
            {filteredMonthGrants.length === 0 ? (
              <p className="text-muted">لا يوجد موظفون مطابقون للبحث</p>
            ) : (
              <div className="hr-overtime-grant__month-table-wrap">
                <table className="hr-overtime-grant__month-table">
                  <thead>
                    <tr>
                      <th>الموظف</th>
                      <th>الكود</th>
                      <th>الساعات</th>
                      <th>النوع</th>
                      <th>الفترة</th>
                      <th>الحالة</th>
                      <th>منحه</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMonthGrants.map((row) => (
                      <tr key={row.id}>
                        <td data-label="الموظف">{row.employeeName || "—"}</td>
                        <td data-label="الكود">{row.employeeCode || "—"}</td>
                        <td data-label="الساعات">{row.hours}</td>
                        <td data-label="النوع">{settlementLabel(row.settlement)}</td>
                        <td data-label="الفترة">{formatGrantPeriod(row)}</td>
                        <td data-label="الحالة">
                          <GrantStatusBadge status={row.status} />
                        </td>
                        <td data-label="منحه">{row.createdByName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <div className="hr-overtime-grant__grid">
        <section className="hr-overtime-grant__panel">
          <h2>منح أوفر تايم يدوي</h2>
          <label className="hr-overtime-grant__search-label" htmlFor="ot-employee-search">
            بحث عن موظف
          </label>
          <input
            id="ot-employee-search"
            type="search"
            className="form-control mb-2"
            placeholder="بحث بالاسم أو الكود..."
            value={searchTerm}
            onChange={(ev) => setSearchTerm(ev.target.value)}
          />
          <div className="hr-overtime-grant__list">
            {filteredEmployees.map((emp) => (
              <button
                key={emp.id}
                type="button"
                className={`hr-overtime-grant__emp ${
                  selectedEmployee?.id === emp.id ? "is-selected" : ""
                }`}
                onClick={() => setSelectedEmployee(emp)}
              >
                <strong>{emp.name || emp.fullName}</strong>
                <span>{emp.code || emp.employeeCode || "—"}</span>
              </button>
            ))}
            {filteredEmployees.length === 0 ? (
              <p className="text-muted">لا يوجد موظفون مطابقون</p>
            ) : null}
          </div>
        </section>

        <section className="hr-overtime-grant__panel">
          <h2>تفاصيل الإضافي اليدوي</h2>
          <form onSubmit={handleSubmit} className="hr-overtime-grant__form">
            <div className="mb-3">
              <label className="form-label">الموظف المختار</label>
              <div className="form-control-plaintext">
                {selectedEmployee
                  ? `${selectedEmployee.name || selectedEmployee.fullName} (${
                      selectedEmployee.code || "—"
                    })`
                  : "— لم يُختر بعد"}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="ot-hours">
                عدد الساعات
              </label>
              <input
                id="ot-hours"
                type="number"
                className="form-control"
                min="0.25"
                step="0.25"
                max="24"
                value={hours}
                onChange={(ev) => setHours(ev.target.value)}
                required
              />
              <div className="form-text">
                {settlement === "COMPENSATORY"
                  ? `${compensatoryOvertimeRateHint(
                      overtimeRates.overtimeCompensatoryHourMultiplier,
                    )} — لنفس الشهر فقط${
                      Number(hours) > 0
                        ? ` (المدخل يُحسب ${applyOvertimeHourMultiplier(
                            Number(hours),
                            overtimeRates.overtimeCompensatoryHourMultiplier,
                          )} ساعة رصيد)`
                        : ""
                    }`
                  : `${paidOvertimeRateHint(
                      overtimeRates.overtimePaidHourMultiplier,
                    )}${
                      Number(hours) > 0
                        ? ` (المدخل يُحسب ${applyOvertimeHourMultiplier(
                            Number(hours),
                            overtimeRates.overtimePaidHourMultiplier,
                          )} ساعة في المرتب)`
                        : ""
                    }`}
              </div>
            </div>
            <fieldset className="mb-3 hr-overtime-grant__choice">
              <legend className="form-label">نوع المنح</legend>
              <label className="hr-overtime-grant__choice-option">
                <input
                  type="radio"
                  name="ot-settlement"
                  value="PAID_OT"
                  checked={settlement === "PAID_OT"}
                  onChange={() => setSettlement("PAID_OT")}
                />
                صرف مالي (أوفر تايم في المرتب) —{" "}
                {paidOvertimeRateHint(overtimeRates.overtimePaidHourMultiplier)}
              </label>
              <label className="hr-overtime-grant__choice-option">
                <input
                  type="radio"
                  name="ot-settlement"
                  value="COMPENSATORY"
                  checked={settlement === "COMPENSATORY"}
                  onChange={() => setSettlement("COMPENSATORY")}
                />
                ساعات إضافية (رصيد إذن تعويضي) —{" "}
                {compensatoryOvertimeRateHint(
                  overtimeRates.overtimeCompensatoryHourMultiplier,
                )}
              </label>
            </fieldset>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="ot-start">
                  من تاريخ
                </label>
                <input
                  id="ot-start"
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(ev) => {
                    setStartDate(ev.target.value);
                    if (!endDate) setEndDate(ev.target.value);
                  }}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="ot-end">
                  إلى تاريخ
                </label>
                <input
                  id="ot-end"
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(ev) => setEndDate(ev.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="ot-reason">
                السبب (اختياري)
              </label>
              <textarea
                id="ot-reason"
                className="form-control"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(ev) => setReason(ev.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting
                ? "جاري الإرسال..."
                : isHrApprover
                  ? "منح واعتماد"
                  : "إرسال للموارد البشرية"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
